// The imagery under the Map view. The handoff draws boundaries "on
// satellite imagery", but no imagery provider is chosen (its licence and
// cost are the business's to decide), so the source is configuration:
// VITE_MAP_TILE_URL (an {z}/{x}/{y} template), VITE_MAP_TILE_ATTRIBUTION and
// VITE_MAP_TILE_MAX_ZOOM. With none set the page uses the OpenStreetMap
// street tiles the handoff names for Product Activity's map, and says it is
// a street map, not imagery.
//
// With VITE_GOOGLE_MAPS_KEY set, Google's satellite imagery goes under the
// street tiles with a Map / Satellite switch (baseLayers.ts), and the maps
// zoom to its finer limit, the street tiles scaled up past their own.

export interface TileSource {
  url: string
  attribution: string
  maxZoom: number
  // The finest zoom the tiles themselves have, where maxZoom goes past it.
  nativeZoom?: number
  // True when a deployment configured its own source.
  configured: boolean
  // Google's satellite imagery is under the tiles, with a switch.
  satellite?: boolean
}

export const SATELLITE_MAX_ZOOM = 21

type Env = {
  VITE_MAP_TILE_URL?: string
  VITE_MAP_TILE_ATTRIBUTION?: string
  VITE_MAP_TILE_MAX_ZOOM?: string
  VITE_GOOGLE_MAPS_KEY?: string
}

export const googleMapsKey = (env: Env = import.meta.env) => (env.VITE_GOOGLE_MAPS_KEY || '').trim()

export const OSM: TileSource = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxZoom: 19,
  configured: false,
}

export function tileSource(env: Env = import.meta.env): TileSource {
  const url = env.VITE_MAP_TILE_URL?.trim()
  const max = Number(env.VITE_MAP_TILE_MAX_ZOOM)
  const own = url
    ? { url, attribution: env.VITE_MAP_TILE_ATTRIBUTION?.trim() || '', maxZoom: Number.isInteger(max) && max >= 0 && max <= 22 ? max : 19, configured: true }
    : OSM
  if (!googleMapsKey(env)) return own
  return { ...own, nativeZoom: own.maxZoom, maxZoom: Math.max(own.maxZoom, SATELLITE_MAX_ZOOM), configured: true, satellite: true }
}

// A stored zoom may be finer than the imagery goes; the view clamps, the
// stored value stays (DECISIONS, site identity).
export const clampZoom = (zoom: number, t: TileSource) => Math.max(0, Math.min(zoom, t.maxZoom))
